/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-28 16:07:55
 *
 *******************************************************/
// @fow

import _ from 'lodash';
import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';

import apiService from 'src/services/api-service';
import {
  GET_FILTER_SETTINGS_REQUEST,
  GET_FILTER_SETTINGS_SUCCESS,
  GET_FILTER_SETTINGS_FAILURE,
  UPDATE_FILTER_SETTINGS_REQUEST,
  UPDATE_FILTER_SETTINGS_SUCCESS,
  UPDATE_FILTER_SETTINGS_FAILURE,
  CLEAR_CACHE_FILTER_SETTINGS_REQUEST,
  CLEAR_CACHE_FILTER_SETTINGS_SUCCESS,
  CLEAR_CACHE_FILTER_SETTINGS_FAILURE
} from 'stores/flow-type/filter-settings.type';
import { FilterSettingsType } from 'src/models/filter-settings';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';
import { trackFSEvent_UpdateFilterSettings } from 'src/integrations/fullStory';
import { getAnalyticDataFromStorage } from 'src/helpers/analytics-v2-helpers';

/**
 * call to get filter settings api
 * @return {object} The RSAA object for API middleware
 */
export const getFilterSettings = (productDetails = false, i18n: I18n, adminAppDisplay = false) => {
  const queries = { productDetails, adminAppDisplay };
  return apiService.sendRequest('admin-v3.filter-settings.get-filter-settings', { queries }, [
    GET_FILTER_SETTINGS_REQUEST,
    {
      type: GET_FILTER_SETTINGS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { ...data };
      }
    },
    {
      type: GET_FILTER_SETTINGS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.FilterSettings.Actions.getFilterSettings'),
          i18n
        )
    }
  ]);
};

/**
 * filter all product data which is stored in shop metafield, only save handle or id
 * when need to use detail of products, use productDetails flag
 * @param {*} settings
 */
const formatBody = (settings: Object) => {
  // save handle for product on suggestion no result feature
  let data = _.get(settings, 'search.suggestionNoResult.products.data', []);
  if (!_.isEmpty(data) && isArrayObject(data)) {
    const handles = _.map(data, 'handle');
    _.set(settings, 'search.suggestionNoResult.products.data', handles);
  }

  // save handle for product backup on suggestion no result feature
  data = _.get(settings, 'search.suggestionNoResult.products.backup', []);
  if (!_.isEmpty(data) && isArrayObject(data)) {
    const handles = _.map(data, 'handle');
    _.set(settings, 'search.suggestionNoResult.products.backup', handles);
  }

  data = _.get(settings, 'search.searchPanelBlocks.mostPopularProducts.productList', []);
  if (!_.isEmpty(data) && isArrayObject(data)) {
    const handles = _.map(data, 'handle');
    _.set(settings, 'search.searchPanelBlocks.mostPopularProducts.productList', handles);
  }

  data = _.get(settings, 'search.searchPanelBlocks.mostPopularProducts.backup', []);
  if (!_.isEmpty(data) && isArrayObject(data)) {
    const handles = _.map(data, 'handle');
    _.set(settings, 'search.searchPanelBlocks.mostPopularProducts.backup', handles);
  }

  data = _.get(settings, 'search.searchBoxOnclick.productSuggestion.data', []);
  if (!_.isEmpty(data) && isArrayObject(data)) {
    const productIds = _.map(data, 'id');
    _.set(settings, 'search.searchBoxOnclick.productSuggestion.data', productIds);
  }

  data = _.get(settings, 'search.searchBoxOnclick.productSuggestion.backup', []);
  if (!_.isEmpty(data) && isArrayObject(data)) {
    const productIds = _.map(data, 'id');
    _.set(settings, 'search.searchBoxOnclick.productSuggestion.backup', productIds);
  }

  return settings;
};

const isArrayObject = (data: any[]) => {
  return _.some(data, item => _.isObject(item));
};

/**
 * call to update filter settings api
 * @return {object} The RSAA object for API middleware
 */
export const updateFilterSettings = (
  data: FilterSettingsType,
  type = '',
  metadata = {},
  i18n: I18n
) => {
  // Prepare Body
  if (type === 'searchPageDisplay' || type === 'instantSearchDisplay') {
    initSearchTermsAndProducts(data);
  }
  const dataBody = { requestParams: { data: formatBody(data), source: type } };

  return apiService.sendRequest(
    'admin-v3.filter-settings.update-filter-settings',
    { body: dataBody },
    [
      UPDATE_FILTER_SETTINGS_REQUEST,
      {
        type: UPDATE_FILTER_SETTINGS_SUCCESS,
        payload: async (action, state, res) => {
          let message = '';
          switch (type) {
            case 'instantSearchDisplay':
              message = i18n.translate(
                'AdminHTML.FilterSettings.Actions.updateFilterSettings_Success_InstantSearchDisplay'
              );
              break;
            case 'searchPageDisplay':
              message = i18n.translate(
                'AdminHTML.FilterSettings.Actions.updateFilterSettings_Success_SearchPageDisplay'
              );
              break;
            case 'defaultLayoutSettings':
              message = i18n.translate(
                'AdminHTML.FilterSettings.Actions.updateFilterSettings_Success_DefaultLayoutSettings'
              );
              break;
            case 'translation':
              message = i18n.translate(
                'AdminHTML.FilterSettings.Actions.updateFilterSettings_Success_Translation'
              );
              break;
            case 'filterLayout':
              message = i18n.translate(
                'AdminHTML.FilterSettings.Actions.updateFilterSettings_Success_FilterLayout'
              );
              break;
            default:
              message = i18n.translate(
                'AdminHTML.FilterSettings.Actions.updateFilterSettings_Success_Default'
              );
          }
          const data = await processRes(res, 'custom-with-sync', message, i18n);
          const {
            isEnableSuggestionTracking = false,
            suggestionsList = {
              productSuggestionsList: [],
              searchBoxProductSuggestionsList: [],
              productBackup: []
            }
          } = metadata;

          mixpanelV3Helper.trackUpdateFilterSettings(type, {
            ...dataBody.requestParams.data,
            isEnableSuggestionTracking
          });

          // Track Full Story event
          trackFSEvent_UpdateFilterSettings(type, {
            ...dataBody.requestParams.data,
            isEnableSuggestionTracking
          });

          return { ...data, suggestionsList };
        }
      },
      {
        type: UPDATE_FILTER_SETTINGS_FAILURE,
        payload: async (action, state, res) => {
          let message = '';
          switch (type) {
            case 'instantSearchDisplay':
              message = i18n.translate(
                'AdminHTML.FilterSettings.Actions.updateFilterSettings_Failure_InstantSearchDisplay'
              );
              break;
            case 'searchPageDisplay':
              message = i18n.translate(
                'AdminHTML.FilterSettings.Actions.updateFilterSettings_Failure_SearchPageDisplay'
              );
              break;
            case 'defaultLayoutSettings':
              message = i18n.translate(
                'AdminHTML.FilterSettings.Actions.updateFilterSettings_Failure_DefaultLayoutSettings'
              );
              break;
            case 'translation':
              message = i18n.translate(
                'AdminHTML.FilterSettings.Actions.updateFilterSettings_Failure_Translation'
              );
              break;
            case 'filterLayout':
              message = i18n.translate(
                'AdminHTML.FilterSettings.Actions.updateFilterSettings_Failure_FilterLayout'
              );
              break;
            default:
              message = i18n.translate(
                'AdminHTML.FilterSettings.Actions.updateFilterSettings_Failure_Default'
              );
          }
          await processRes(res, 'error', message, i18n);
        }
      }
    ]
  );
};

/**
 * call to clear cache filter settings api
 * @return {object} The RSAA object for API middleware
 */
export const clearCache = (data: FilterSettingsType, i18n: I18n) => {
  // Prepare Body
  const dataBody = { requestParams: { data, source: 'clearCache' } };

  return apiService.sendRequest(
    'admin-v3.filter-settings.update-filter-settings',
    { body: dataBody },
    [
      CLEAR_CACHE_FILTER_SETTINGS_REQUEST,
      {
        type: CLEAR_CACHE_FILTER_SETTINGS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'info',
            i18n.translate('AdminHTML.FilterSettings.Actions.clearCache_Success'),
            i18n
          );

          mixpanelV3Helper.trackEventData('Clear Cache Filter Settings', {
            feature: 'Filter Settings'
          });

          return { data };
        }
      },
      {
        type: CLEAR_CACHE_FILTER_SETTINGS_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.FilterSettings.Actions.clearCache_Failure'),
            i18n
          );

          return errorResult;
        }
      }
    ]
  );
};

/**
 * filter all product data which is stored in shop metafield, only save handle or id
 * when need to use detail of products, use productDetails flag
 * @param {*} settings
 */
const initSearchTermsAndProducts = (settings: Object) => {
  const { topProducts = [], topSearchTerms = [] } = getAnalyticDataFromStorage();
  const productsData = topProducts.map(e => ({
    handle: e.productHandle,
    id: e.productId,
    images: e.productImages,
    title: e.productName
  }));
  const searchTermsData = topSearchTerms.map(e => e.searchTerm);

  // Suggestion no results
  const suggestionNoResultProducts = _.get(settings, 'search.suggestionNoResult.products', {});
  if (suggestionNoResultProducts?.type === 'based_on_data') {
    _.set(settings, 'search.suggestionNoResult.products.data', productsData);
  }
  const suggestionNoResultSearchTerms = _.get(
    settings,
    'search.suggestionNoResult.search_terms',
    {}
  );
  if (suggestionNoResultSearchTerms?.type === 'based_on_data') {
    _.set(settings, 'search.suggestionNoResult.search_terms.data', searchTermsData);
  }

  // Search box on click
  const searchBoxOnClickProducts = _.get(settings, 'search.searchBoxOnclick.productSuggestion', {});
  if (searchBoxOnClickProducts?.type === 'based_on_data') {
    _.set(settings, 'search.searchBoxOnclick.productSuggestion.data', productsData);
  }

  const searchBoxOnClickSearchTerms = _.get(
    settings,
    'search.searchBoxOnclick.searchTermSuggestion',
    {}
  );
  if (searchBoxOnClickSearchTerms?.type === 'based_on_data') {
    _.set(settings, 'search.searchBoxOnclick.searchTermSuggestion.data', searchTermsData);
  }

  // Search panel blocks
  const searchPanelBlocksProducts = _.get(
    settings,
    'search.searchPanelBlocks.mostPopularProducts',
    {}
  );
  if (searchPanelBlocksProducts?.type === 'based_on_data') {
    _.set(settings, 'search.searchPanelBlocks.mostPopularProducts.productList', productsData);
  }

  const searchPanelBlocksSearchTerms = _.get(
    settings,
    'search.searchPanelBlocks.searchTermSuggestions',
    {}
  );
  if (searchPanelBlocksSearchTerms?.type === 'based_on_data') {
    _.set(
      settings,
      'search.searchPanelBlocks.searchTermSuggestions.searchTermList',
      topSearchTerms.map(e => e.searchTerm)
    );
  }

  return settings;
};
